import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarDays } from "lucide-react";
import SEO from "@/components/SEO";

import manikuereImage from "@/assets/manikuere-nagelschere-studio.webp";
import facialImage from "@/assets/facial-behandlung-nahaufnahme.webp";
import haarentfernungImage from "@/assets/haarentfernung-glatte-haut-koerper.webp";
import laserImage from "@/assets/diodenlaser-haarentfernung-beine-nuernberg.webp";

const articles = [
  {
    slug: "dauerhafte-haarentfernung-nuernberg-ratgeber",
    title: "Dauerhafte Haarentfernung in Nürnberg – Der komplette Ratgeber (2025)",
    excerpt: "Alles über dauerhafte Haarentfernung: Methoden, Kosten, Ablauf, Schmerzen & Ergebnisse. Mit Preisliste, Kostenvergleich Rasieren vs. Laser und 10 FAQ.",
    category: "Haarentfernung",
    readTime: "12 Min.",
    date: "4. April 2026",
    image: laserImage,
  },
  {
    slug: "dauerhafte-haarentfernung-ipl-laser-unterschied",
    title: "IPL vs. Laser: Was ist besser für dauerhafte Haarentfernung?",
    excerpt: "IPL oder Laser – was funktioniert wirklich? Ein ehrlicher Vergleich beider Methoden mit Vor- und Nachteilen, Kosten und für wen welche Methode geeignet ist.",
    category: "Haarentfernung",
    readTime: "9 Min.",
    date: "10. März 2025",
    image: haarentfernungImage,
  },
  {
    slug: "gelnaegel-kosten-nuernberg",
    title: "Was kosten Gelnägel in Nürnberg? Preise, Qualität & worauf du achten solltest",
    excerpt: "Gelnägel-Preise in Nürnberg variieren stark – von 30 € bis über 100 €. Wir erklären, was den Unterschied ausmacht und warum billig oft teurer wird.",
    category: "Nägel",
    readTime: "8 Min.",
    date: "15. Januar 2025",
    image: manikuereImage,
  },
  {
    slug: "wimpernverlaengerung-pflege-tipps",
    title: "Lash Lifting Pflege: 10 Fehler, die das Ergebnis ruinieren",
    excerpt: "Dein Lash Lifting hält nicht lange? Oft liegt es an der Pflege danach. Die häufigsten Fehler und wie du sie vermeidest.",
    category: "Wimpern",
    readTime: "7 Min.",
    date: "28. Februar 2025",
    image: facialImage,
  },
];

const BlogPage = () => {
  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Beauty-Ratgeber & Tipps aus Nürnberg | 22 Studios Blog"
        description="Praxis-Tipps zu Gelnägeln, Wimpernlifting, Gesichtsbehandlung & Laser-Haarentfernung. Ehrliche Preisvergleiche & Expertenwissen aus unserem Studio."
        canonical="https://22studios.de/nuernberg/blog"
      />
      <AnimatedBackground />
      <Navigation />

      <section className="pt-24 pb-10 md:pt-40 md:pb-24 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-16">
              <span className="text-sm tracking-wide uppercase text-label font-medium block mb-4">
                Beauty Ratgeber
              </span>
              <h1 className="text-display md:text-display-lg font-serif font-light text-foreground">
                Beauty Blog
              </h1>
              <p className="mt-4 text-muted-foreground font-light max-w-xl mx-auto">
                Expertenwissen, ehrliche Tipps und Antworten auf die häufigsten Beauty-Fragen – direkt aus Nürnberg.
              </p>
            </div>

            <div className="space-y-4 md:space-y-8">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  className="group block bg-background rounded-2xl border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Hero Image */}
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-transparent" />
                  </div>

                  <div className="p-4 md:p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary/30 text-foreground">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3.5 h-3.5" />
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-serif font-medium text-foreground group-hover:text-highlight transition-colors mb-2">
                      {article.title}
                    </h2>
                    <p className="text-muted-foreground font-light leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-highlight group-hover:gap-2 transition-all">
                      Weiterlesen <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogPage;
